// src/components/proposal-formats/FormatDocumentSlotsSection.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  deleteFormatDocumentSlot,
  listFormatDocumentSlots,
  reorderFormatDocumentSlots,
  upsertFormatDocumentSlot,
} from "@/lib/opportunity/formatDocumentSlotClient";
import {
  isPersistedDocumentSlotId,
  mergeDocumentSlotsForEditor,
  slugifyDocumentSlotKey,
  type FormatDocumentSlot,
} from "@/lib/opportunity/formatDocumentSlotTypes";
import { FormatDocumentSlotRow } from "./FormatDocumentSlotRow";
import { ProposalFormatCollapsibleSection } from "./ProposalFormatCollapsibleSection";

export type FormatDocumentSlotsSectionProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  reloadTick: number;
  onChanged: () => void;
};

export function FormatDocumentSlotsSection(props: FormatDocumentSlotsSectionProps) {
  const { formatKey, productType, reloadTick, onChanged } = props;
  const [slots, setSlots] = useState<FormatDocumentSlot[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await listFormatDocumentSlots({ format_key: formatKey, product_type: productType });
    setLoading(false);
    if (r.ok) {
      setSlots(
        mergeDocumentSlotsForEditor(
          r.data.slots.map((s) => ({ ...s, format_key: formatKey, product_type: productType })),
          formatKey,
          productType,
        ),
      );
    }
  }, [formatKey, productType]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const displaySlots = useMemo(
    () => [...slots].sort((a, b) => a.sort_order - b.sort_order),
    [slots],
  );

  async function addSlot() {
    const nextOrder = displaySlots.length;
    const label = "Document";
    const r = await upsertFormatDocumentSlot({
      format_key: formatKey,
      product_type: productType,
      slot_key: slugifyDocumentSlotKey(`${label}_${nextOrder}`),
      label,
      helper_text: null,
      sort_order: nextOrder,
    });
    if (r.ok) onChanged();
  }

  async function moveSlot(id: string, direction: "up" | "down") {
    const idx = displaySlots.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= displaySlots.length) return;
    const reordered = [...displaySlots];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swapIdx];
    reordered[swapIdx] = tmp;
    const persistedIds = reordered.map((s) => s.id).filter((sid) => isPersistedDocumentSlotId(sid));
    if (persistedIds.length === 0) return;
    const r = await reorderFormatDocumentSlots({
      format_key: formatKey,
      product_type: productType,
      ordered_ids: persistedIds,
    });
    if (r.ok) onChanged();
  }

  async function removeSlot(id: string): Promise<{ ok: boolean; error?: string }> {
    if (displaySlots.length <= 1) {
      return { ok: false, error: "At least one document slot is required." };
    }

    if (isPersistedDocumentSlotId(id)) {
      const r = await deleteFormatDocumentSlot(id);
      if (r.ok) onChanged();
      return r.ok ? { ok: true } : { ok: false, error: r.error };
    }

    const remaining = displaySlots.filter((s) => s.id !== id);
    for (let i = 0; i < remaining.length; i++) {
      const slot = remaining[i];
      const r = await upsertFormatDocumentSlot({
        format_key: formatKey,
        product_type: productType,
        slot_key: slot.slot_key,
        label: slot.label,
        helper_text: slot.helper_text,
        sort_order: i,
      });
      if (!r.ok) return { ok: false, error: r.error };
    }
    onChanged();
    return { ok: true };
  }

  return (
    <ProposalFormatCollapsibleSection
      title="Document slots"
      description="Upload categories on the opportunity Documents tab. Generic formats start with one slot."
      headerRight={
        <button
          type="button"
          onClick={() => void addSlot()}
          className="px-2 py-1 rounded-lg border border-gray-300 text-[11px] hover:bg-gray-50 dark:border-zinc-600 dark:hover:bg-zinc-800 shrink-0"
        >
          + Add slot
        </button>
      }
    >
      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-2">
          {displaySlots.map((slot, idx) => (
            <FormatDocumentSlotRow
              key={slot.id}
              slot={slot}
              productType={productType}
              canMoveUp={idx > 0}
              canMoveDown={idx < displaySlots.length - 1}
              canDelete={displaySlots.length > 1}
              onSave={async (payload) => {
                const r = await upsertFormatDocumentSlot(payload);
                if (r.ok) onChanged();
                return r.ok ? { ok: true } : { ok: false, error: r.error };
              }}
              onDelete={
                displaySlots.length > 1
                  ? async (id) => removeSlot(id)
                  : undefined
              }
              onMove={(dir) => void moveSlot(slot.id, dir)}
            />
          ))}
        </div>
      )}
    </ProposalFormatCollapsibleSection>
  );
}
